import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, TruckIcon, Download, Lock, Unlock, FileText, ClipboardList, RefreshCw } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const FURGON_STATUSES = [
  { value: 'recogido', label: 'Recogido', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  { value: 'en_transito', label: 'En Transito', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'embarque_maritimo', label: 'Embarque Maritimo', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  { value: 'revision_aduana', label: 'Revision Aduana', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'almacen_rd', label: 'Almacen RD', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'camino_entrega', label: 'En Camino a Entrega', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'entregado', label: 'Entregado', color: 'bg-green-100 text-green-800 border-green-300' },
];

const Furgones = () => {
  const [furgones, setFurgones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFurgon, setEditingFurgon] = useState(null);
  const [manifiestoDialog, setManifiestoDialog] = useState(false);
  const [manifiestoFurgon, setManifiestoFurgon] = useState(null);
  const [bookingNumber, setBookingNumber] = useState('');
  const [formData, setFormData] = useState({
    numero: '',
    estado: 'en_camino',
    fecha_salida: '',
    fecha_llegada: '',
    notas: ''
  });

  useEffect(() => { fetchFurgones(); }, []);

  const fetchFurgones = async () => {
    try {
      const response = await api.get('/furgones');
      setFurgones(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar furgones');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        numero: formData.numero,
        estado: formData.estado,
        fecha_salida: formData.fecha_salida || null,
        fecha_llegada: formData.fecha_llegada || null,
        notas: formData.notas || null
      };
      if (editingFurgon) {
        await api.put(`/furgones/${editingFurgon.id}`, payload);
        toast.success('Furgon actualizado');
      } else {
        await api.post('/furgones', payload);
        toast.success('Furgon creado');
      }
      fetchFurgones();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar furgon');
    }
  };

  const handleEdit = (furgon) => {
    setEditingFurgon(furgon);
    setFormData({
      numero: furgon.numero,
      estado: furgon.estado,
      fecha_salida: furgon.fecha_salida || '',
      fecha_llegada: furgon.fecha_llegada || '',
      notas: furgon.notas || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este furgon?')) return;
    try {
      await api.delete(`/furgones/${id}`);
      toast.success('Furgon eliminado');
      fetchFurgones();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const toggleFurgon = async (id) => {
    try {
      const res = await api.put(`/furgones/${id}/toggle`);
      toast.success(res.data.message);
      fetchFurgones();
    } catch (error) { toast.error('Error al cambiar estado'); }
  };

  const resetForm = () => {
    setEditingFurgon(null);
    setFormData({ numero: '', estado: 'en_camino', fecha_salida: '', fecha_llegada: '', notas: '' });
  };

  const downloadManifiesto = async (numero) => {
    try {
      const response = await api.get(`/reports/manifiesto/${numero}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `manifiesto_${numero}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Manifiesto descargado');
    } catch (error) { toast.error('Error al descargar manifiesto'); }
  };

  const downloadFacturasPDF = async (numero) => {
    try {
      const response = await api.get(`/reports/furgon/${numero}/facturas-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('PDF de facturas generado');
    } catch (error) { toast.error('Error al generar PDF de facturas'); }
  };

  const openManifiestoDialog = (furgon) => {
    setManifiestoFurgon(furgon);
    setBookingNumber('');
    setManifiestoDialog(true);
  };

  const changeFurgonStatus = async (furgonId, newEstado) => {
    try {
      const res = await api.put(`/furgones/${furgonId}/estado`, { estado: newEstado });
      toast.success(res.data.message);
      fetchFurgones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const getStatusInfo = (estado) => {
    return FURGON_STATUSES.find(s => s.value === estado) || FURGON_STATUSES[0];
  };

  const downloadManifiestoSA13 = async () => {
    if (!bookingNumber.trim()) {
      toast.error('Ingresa el numero de booking');
      return;
    }
    try {
      const response = await api.get(`/reports/furgon/${manifiestoFurgon.numero}/manifiesto-sa13`, {
        params: { booking: bookingNumber.trim() },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Manifiesto_Furgon_${manifiestoFurgon.numero}_Booking_${bookingNumber.trim()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Manifiesto SA13 descargado');
      setManifiestoDialog(false);
    } catch (error) { toast.error('Error al generar manifiesto SA13'); }
  };

  const openCount = furgones.filter(f => f.abierto !== false).length;
  const closedCount = furgones.filter(f => f.abierto === false).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-3 sm:p-6 pb-24">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-2 border-indigo-200 bg-indigo-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-indigo-800">{furgones.length}</p>
              <p className="text-sm font-semibold text-indigo-600">Total</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-4 text-center">
              <Unlock className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-800">{openCount}</p>
              <p className="text-sm font-semibold text-green-600">Abiertos</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <Lock className="h-5 w-5 text-red-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-red-800">{closedCount}</p>
              <p className="text-sm font-semibold text-red-600">Cerrados</p>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-xl border-2 border-indigo-100">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-white/20 rounded-lg flex-shrink-0"><TruckIcon className="h-6 w-6 sm:h-8 sm:w-8" /></div>
                <div className="min-w-0">
                  <CardTitle className="text-xl sm:text-2xl font-bold">Furgones</CardTitle>
                  <p className="text-indigo-100 text-xs sm:text-sm">{furgones.length} registrados</p>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-furgon-btn" className="h-10 sm:h-12 px-3 sm:px-6 bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg font-bold text-sm sm:text-base flex-shrink-0" size="lg">
                    <Plus className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" /> <span className="hidden sm:inline">Nuevo</span> Furgon
                  </Button>
                </DialogTrigger>
                <DialogContent aria-describedby="furgon-dialog-desc">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">{editingFurgon ? 'Editar' : 'Nuevo'} Furgon</DialogTitle>
                    <p id="furgon-dialog-desc" className="text-sm text-gray-500">Complete los datos del furgon</p>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-base font-semibold">Numero de Furgon *</Label>
                      <Input data-testid="furgon-numero-input" value={formData.numero} onChange={(e) => setFormData({...formData, numero: e.target.value})} placeholder="F-001" required className="h-12 mt-1 text-lg" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold">Estado</Label>
                      <Select value={formData.estado} onValueChange={(value) => setFormData({...formData, estado: value})}>
                        <SelectTrigger className="h-12 mt-1 text-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en_camino">En Camino</SelectItem>
                          <SelectItem value="aduana">Aduana</SelectItem>
                          <SelectItem value="almacen">Almacen</SelectItem>
                          <SelectItem value="en_ruta">En Ruta</SelectItem>
                          <SelectItem value="completado">Completado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-base font-semibold">Fecha Salida</Label>
                        <Input type="date" value={formData.fecha_salida} onChange={(e) => setFormData({...formData, fecha_salida: e.target.value})} className="h-12 mt-1 text-lg" />
                      </div>
                      <div>
                        <Label className="text-base font-semibold">Fecha Llegada</Label>
                        <Input type="date" value={formData.fecha_llegada} onChange={(e) => setFormData({...formData, fecha_llegada: e.target.value})} className="h-12 mt-1 text-lg" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-base font-semibold">Notas</Label>
                      <Input value={formData.notas} onChange={(e) => setFormData({...formData, notas: e.target.value})} placeholder="Contenedor 40 pies..." className="h-12 mt-1 text-lg" />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button type="button" variant="outline" size="lg" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button data-testid="submit-furgon-btn" type="submit" size="lg" className="bg-gradient-to-r from-indigo-500 to-purple-500 px-8">
                        {editingFurgon ? 'Actualizar' : 'Crear Furgon'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Mobile Cards */}
                <div className="sm:hidden space-y-3">
                  {furgones.length === 0 ? (
                    <div className="text-center py-12">
                      <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No hay furgones</p>
                    </div>
                  ) : furgones.map((furgon) => (
                    <div key={furgon.id} className={`bg-white rounded-xl border-2 p-4 space-y-3 ${furgon.abierto === false ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-black text-indigo-700 text-xl">{furgon.numero}</span>
                        <div className="flex items-center gap-2">
                          {furgon.abierto !== false ? (
                            <Badge className="bg-green-100 text-green-800 border-green-300 border font-bold text-xs"><Unlock className="h-3 w-3 mr-1" />ABIERTO</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border-red-300 border font-bold text-xs"><Lock className="h-3 w-3 mr-1" />CERRADO</Badge>
                          )}
                          <Badge variant="outline" className="text-xs font-bold">{furgon.shipment_count || 0} envios</Badge>
                        </div>
                      </div>
                      <Select value={furgon.estado || 'recogido'} onValueChange={(v) => changeFurgonStatus(furgon.id, v)}>
                        <SelectTrigger data-testid={`furgon-status-${furgon.id}`} className={`h-10 font-bold text-sm border ${getStatusInfo(furgon.estado).color}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FURGON_STATUSES.map(s => (<SelectItem key={s.value} value={s.value}><span className="font-semibold">{s.label}</span></SelectItem>))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>Salida: {furgon.fecha_salida || '-'}</span>
                        <span>Llegada: {furgon.fecha_llegada || '-'}</span>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        <Button data-testid={`toggle-furgon-${furgon.id}`} variant="outline" size="sm" onClick={() => toggleFurgon(furgon.id)}
                          className={`h-9 text-xs ${furgon.abierto !== false ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'}`}>
                          {furgon.abierto !== false ? <><Lock className="h-3 w-3 mr-1" />Cerrar</> : <><Unlock className="h-3 w-3 mr-1" />Abrir</>}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => downloadManifiesto(furgon.numero)} className="h-9 w-9"><Download className="h-4 w-4 text-green-600" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => downloadFacturasPDF(furgon.numero)} className="h-9 w-9"><FileText className="h-4 w-4 text-blue-600" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openManifiestoDialog(furgon)} className="h-9 w-9"><ClipboardList className="h-4 w-4 text-purple-600" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(furgon)} className="h-9 w-9"><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(furgon.id)} className="h-9 w-9"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop Table */}
                <div className="hidden sm:block bg-white rounded-lg border-2 border-gray-100 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-indigo-50 to-purple-50">
                      <TableHead className="font-bold text-gray-900 text-base">Numero</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Estado</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Abierto/Cerrado</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">F. Salida</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">F. Llegada</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Envios</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {furgones.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12">
                        <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No hay furgones registrados</p>
                      </TableCell></TableRow>
                    ) : (
                      furgones.map((furgon) => (
                        <TableRow key={furgon.id} className={`hover:bg-indigo-50/50 transition-colors ${furgon.abierto === false ? 'bg-red-50/30' : ''}`}>
                          <TableCell className="font-bold text-lg">{furgon.numero}</TableCell>
                          <TableCell>
                            <Select
                              value={furgon.estado || 'recogido'}
                              onValueChange={(v) => changeFurgonStatus(furgon.id, v)}
                            >
                              <SelectTrigger
                                data-testid={`furgon-status-${furgon.id}`}
                                className={`h-9 w-48 font-bold text-sm border ${getStatusInfo(furgon.estado).color}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FURGON_STATUSES.map(s => (
                                  <SelectItem key={s.value} value={s.value}>
                                    <span className={`font-semibold`}>{s.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {furgon.abierto !== false ? (
                              <Badge className="bg-green-100 text-green-800 border-green-300 border font-bold">
                                <Unlock className="h-3 w-3 mr-1" /> ABIERTO
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-red-300 border font-bold">
                                <Lock className="h-3 w-3 mr-1" /> CERRADO
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-base">{furgon.fecha_salida || '-'}</TableCell>
                          <TableCell className="text-base">{furgon.fecha_llegada || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-sm font-bold">{furgon.shipment_count || 0} envios</Badge></TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                data-testid={`toggle-furgon-${furgon.id}`}
                                variant="outline" size="sm"
                                onClick={() => toggleFurgon(furgon.id)}
                                className={`h-9 ${furgon.abierto !== false ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                title={furgon.abierto !== false ? 'Cerrar Furgon' : 'Abrir Furgon'}
                              >
                                {furgon.abierto !== false ? <><Lock className="h-4 w-4 mr-1" /> Cerrar</> : <><Unlock className="h-4 w-4 mr-1" /> Abrir</>}
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => downloadManifiesto(furgon.numero)} title="Manifiesto Excel" className="h-9 w-9" data-testid={`download-manifest-${furgon.id}`}>
                                <Download className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => downloadFacturasPDF(furgon.numero)} title="Todas las Facturas PDF" className="h-9 w-9" data-testid={`download-facturas-pdf-${furgon.id}`}>
                                <FileText className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openManifiestoDialog(furgon)} title="Manifiesto SA13" className="h-9 w-9" data-testid={`manifiesto-sa13-${furgon.id}`}>
                                <ClipboardList className="h-4 w-4 text-purple-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(furgon)} className="h-9 w-9">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(furgon.id)} className="h-9 w-9">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Manifiesto SA13 Dialog */}
        <Dialog open={manifiestoDialog} onOpenChange={setManifiestoDialog}>
          <DialogContent className="max-w-md" aria-describedby="manifiesto-sa13-desc">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-purple-600" />
                Manifiesto SA13 - Furgon {manifiestoFurgon?.numero}
              </DialogTitle>
              <p id="manifiesto-sa13-desc" className="text-sm text-gray-500">
                Ingresa el numero de booking para generar el manifiesto
              </p>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                <p className="text-sm text-purple-600 font-semibold">Furgon: {manifiestoFurgon?.numero}</p>
                <p className="text-sm text-gray-500">{manifiestoFurgon?.shipment_count || 0} envios asignados</p>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-bold">Numero de Booking / Doc. de Embarque *</Label>
                <Input
                  data-testid="booking-number-input"
                  value={bookingNumber}
                  onChange={(e) => setBookingNumber(e.target.value)}
                  placeholder="Ej: smlu, maeu, etc."
                  className="h-12 text-lg border-2 focus:border-purple-500"
                  onKeyDown={(e) => { if (e.key === 'Enter') downloadManifiestoSA13(); }}
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setManifiestoDialog(false)} className="flex-1 h-11">
                  Cancelar
                </Button>
                <Button
                  data-testid="download-manifiesto-sa13-btn"
                  onClick={downloadManifiestoSA13}
                  disabled={!bookingNumber.trim()}
                  className="flex-1 h-11 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                >
                  <Download className="mr-2 h-4 w-4" /> Descargar Manifiesto
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Furgones;
